import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from './config';
import { COLLECTIONS, validateDocument, getDefaultValues } from './schema';

// Collection references (using schema constants)
export const collections = COLLECTIONS;

// Generic CRUD operations
export const database = {
  // Create a new document
  async create(collectionName, data) {
    try {
      // Get default values and merge with provided data
      const defaults = getDefaultValues(collectionName);
      const documentData = {
        ...defaults,
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Validate the document before creating
      validateDocument(collectionName, documentData);

      const docRef = await addDoc(collection(db, collectionName), documentData);
      return { id: docRef.id, ...documentData };
    } catch (error) {
      console.error('Error creating document:', error);
      throw error;
    }
  },

  // Read a single document
  async read(collectionName, docId) {
    try {
      const docRef = doc(db, collectionName, docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Document not found');
      }
    } catch (error) {
      console.error('Error reading document:', error);
      throw error;
    }
  },

  // Read all documents in a collection
  async readAll(collectionName, constraints = []) {
    try {
      let q = collection(db, collectionName);
      
      // Apply constraints if provided
      if (constraints.length > 0) {
        q = query(q, ...constraints);
      }
      
      const querySnapshot = await getDocs(q);
      const documents = [];
      
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() });
      });
      
      return documents;
    } catch (error) {
      console.error('Error reading documents:', error);
      throw error;
    }
  },

  // Update a document
  async update(collectionName, docId, data) {
    try {
      // Validate the update data
      validateDocument(collectionName, data);

      const docRef = doc(db, collectionName, docId);
      const updateData = {
        ...data,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(docRef, updateData);
      return { id: docId, ...updateData };
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  },

  // Delete a document
  async delete(collectionName, docId) {
    try {
      const docRef = doc(db, collectionName, docId);
      await deleteDoc(docRef);
      return { id: docId };
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }
};

// Specific functions for your landing page
export const contactService = {
  // Submit contact form
  async submitContact(formData) {
    return await database.create(collections.CONTACTS, {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
      subject: formData.subject || 'Contact Form Submission',
      source: 'landing_page',
      category: 'general',
      priority: 'medium',
      status: 'new',
      userAgent: navigator.userAgent,
      referrer: document.referrer
    });
  },

  // Get all contact submissions
  async getAllContacts() {
    return await database.readAll(collections.CONTACTS, [
      orderBy('createdAt', 'desc')
    ]);
  },

  // Get contacts by status
  async getContactsByStatus(status) {
    return await database.readAll(collections.CONTACTS, [
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    ]);
  },

  // Update contact status
  async updateContactStatus(contactId, status, response = null) {
    const updateData = { status };
    if (response) {
      updateData.response = response;
      updateData.responseDate = serverTimestamp();
    }
    return await database.update(collections.CONTACTS, contactId, updateData);
  }
};

export const analyticsService = {
  // Generate or retrieve session ID
  getSessionId() {
    let sessionId = sessionStorage.getItem('hemicron_session_id');
    if (!sessionId) {
      sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('hemicron_session_id', sessionId);
    }
    return sessionId;
  },

  // Get device and browser information
  getDeviceInfo() {
    const userAgent = navigator.userAgent;
    let deviceType = 'desktop';
    let browser = 'unknown';
    let os = 'unknown';

    // Detect device type
    if (/Mobile|Android|iPhone|iPad/.test(userAgent)) {
      deviceType = /iPad/.test(userAgent) ? 'tablet' : 'mobile';
    }

    // Detect browser
    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari')) browser = 'Safari';
    else if (userAgent.includes('Edge')) browser = 'Edge';

    // Detect OS
    if (userAgent.includes('Windows')) os = 'Windows';
    else if (userAgent.includes('Mac')) os = 'macOS';
    else if (userAgent.includes('Linux')) os = 'Linux';
    else if (userAgent.includes('Android')) os = 'Android';
    else if (userAgent.includes('iOS')) os = 'iOS';

    return { deviceType, browser, os };
  },

  // Get URL parameters
  getUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      utmSource: urlParams.get('utm_source'),
      utmMedium: urlParams.get('utm_medium'),
      utmCampaign: urlParams.get('utm_campaign'),
      utmTerm: urlParams.get('utm_term'),
      utmContent: urlParams.get('utm_content')
    };
  },

  // Track page views with enhanced data
  async trackPageView(pageName, additionalData = {}) {
    const sessionId = this.getSessionId();
    const deviceInfo = this.getDeviceInfo();
    const urlParams = this.getUrlParams();
    
    return await database.create(collections.ANALYTICS, {
      type: 'page_view',
      page: pageName,
      sessionId,
      timestamp: serverTimestamp(),
      userAgent: navigator.userAgent,
      referrer: document.referrer,
      deviceType: deviceInfo.deviceType,
      browser: deviceInfo.browser,
      os: deviceInfo.os,
      url: window.location.href,
      ...urlParams,
      ...additionalData
    });
  },

  // Track user interactions
  async trackInteraction(action, element, additionalData = {}) {
    return await database.create(collections.ANALYTICS, {
      type: 'interaction',
      action,
      element,
      timestamp: serverTimestamp(),
      userAgent: navigator.userAgent,
      ...additionalData
    });
  },

  // Track form submissions
  async trackFormSubmission(formName, success = true, additionalData = {}) {
    return await database.create(collections.ANALYTICS, {
      type: 'form_submission',
      action: 'submit',
      element: formName,
      additionalData: {
        success,
        ...additionalData
      },
      timestamp: serverTimestamp(),
      userAgent: navigator.userAgent
    });
  },

  // Track conversions
  async trackConversion(conversionType, value = null, additionalData = {}) {
    return await database.create(collections.ANALYTICS, {
      type: 'conversion',
      action: conversionType,
      additionalData: {
        value,
        ...additionalData
      },
      timestamp: serverTimestamp(),
      userAgent: navigator.userAgent
    });
  },

  // Analytics retrieval functions
  async getPageViews(pageName = null, startDate = null, endDate = null) {
    try {
      // Start with basic query - type filter only
      let constraints = [
        where('type', '==', 'page_view')
      ];

      // Add timestamp ordering
      constraints.push(orderBy('timestamp', 'desc'));

      // Try the full query first
      if (pageName) {
        constraints.push(where('page', '==', pageName));
      }

      if (startDate) {
        constraints.push(where('timestamp', '>=', startDate));
      }

      if (endDate) {
        constraints.push(where('timestamp', '<=', endDate));
      }

      return await database.readAll(collections.ANALYTICS, constraints);
    } catch (indexError) {
      console.warn('Index not ready, using simple query:', indexError.message);
      
      // Fallback: Simple query without complex filters
      const simpleConstraints = [
        where('type', '==', 'page_view'),
        orderBy('timestamp', 'desc')
      ];
      
      const allPageViews = await database.readAll(collections.ANALYTICS, simpleConstraints);
      
      // Filter client-side
      let filteredViews = allPageViews;
      
      if (pageName) {
        filteredViews = filteredViews.filter(pv => pv.page === pageName);
      }
      
      if (startDate) {
        filteredViews = filteredViews.filter(pv => {
          const timestamp = pv.timestamp?.toDate?.() || new Date(pv.timestamp);
          return timestamp >= startDate;
        });
      }
      
      if (endDate) {
        filteredViews = filteredViews.filter(pv => {
          const timestamp = pv.timestamp?.toDate?.() || new Date(pv.timestamp);
          return timestamp <= endDate;
        });
      }
      
      return filteredViews;
    }
  },

  async getPageViewStats(pageName = null, days = 30) {
    try {
      // For now, let's get all page views and filter client-side to avoid index issues
      let pageViews;
      
      try {
        // Try the optimized query first
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - days);
        pageViews = await this.getPageViews(pageName, startDate, endDate);
      } catch (indexError) {
        console.warn('Index not ready, falling back to simple query:', indexError.message);
        // Fallback: Get all page views and filter client-side
        const allPageViews = await this.getPageViews();
        
        // Filter by date range client-side
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - days);
        
        pageViews = allPageViews.filter(pv => {
          const timestamp = pv.timestamp?.toDate?.() || new Date(pv.timestamp);
          return timestamp >= startDate && timestamp <= endDate;
        });
        
        // Filter by page if specified
        if (pageName) {
          pageViews = pageViews.filter(pv => pv.page === pageName);
        }
      }
      
      // Calculate statistics
      const stats = {
        totalViews: pageViews.length,
        uniqueSessions: new Set(pageViews.map(pv => pv.sessionId)).size,
        uniquePages: new Set(pageViews.map(pv => pv.page)).size,
        viewsByPage: {},
        viewsByDevice: {},
        viewsByBrowser: {},
        viewsByDay: {},
        topPages: [],
        averageViewsPerSession: 0
      };

      // Process each page view
      pageViews.forEach(pv => {
        // Views by page
        stats.viewsByPage[pv.page] = (stats.viewsByPage[pv.page] || 0) + 1;
        
        // Views by device
        stats.viewsByDevice[pv.deviceType] = (stats.viewsByDevice[pv.deviceType] || 0) + 1;
        
        // Views by browser
        stats.viewsByBrowser[pv.browser] = (stats.viewsByBrowser[pv.browser] || 0) + 1;
        
        // Views by day
        const date = pv.timestamp?.toDate?.() || new Date(pv.timestamp);
        const dayKey = date.toISOString().split('T')[0];
        stats.viewsByDay[dayKey] = (stats.viewsByDay[dayKey] || 0) + 1;
      });

      // Calculate top pages
      stats.topPages = Object.entries(stats.viewsByPage)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10)
        .map(([page, views]) => ({ page, views }));

      // Calculate average views per session
      stats.averageViewsPerSession = stats.uniqueSessions > 0 
        ? Math.round((stats.totalViews / stats.uniqueSessions) * 100) / 100 
        : 0;

      return stats;
    } catch (error) {
      console.error('Error calculating page view stats:', error);
      throw error;
    }
  },

  async getSessionData(sessionId) {
    try {
      const constraints = [
        where('sessionId', '==', sessionId),
        orderBy('timestamp', 'asc')
      ];

      return await database.readAll(collections.ANALYTICS, constraints);
    } catch (error) {
      console.error('Error retrieving session data:', error);
      throw error;
    }
  },

  async getRealTimeStats() {
    try {
      const now = new Date();
      const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

      const recentViews = await this.getPageViews(null, oneHourAgo, now);
      
      return {
        viewsLastHour: recentViews.length,
        activeSessions: new Set(recentViews.map(pv => pv.sessionId)).size,
        mostViewedPage: recentViews.length > 0 
          ? recentViews.reduce((a, b) => 
              (recentViews.filter(v => v.page === a.page).length > recentViews.filter(v => v.page === b.page).length) ? a : b
            ).page
          : null
      };
    } catch (error) {
      console.error('Error retrieving real-time stats:', error);
      throw error;
    }
  }
};

export const feedbackService = {
  // Submit feedback
  async submitFeedback(feedbackData) {
    return await database.create(collections.FEEDBACK, {
      rating: feedbackData.rating,
      comment: feedbackData.comment,
      category: feedbackData.category || 'general',
      email: feedbackData.email || null,
      userId: feedbackData.userId || null,
      feature: feedbackData.feature || null,
      isPublic: feedbackData.isPublic || false,
      isAnonymous: feedbackData.isAnonymous || false,
      status: 'new'
    });
  },

  // Get all feedback
  async getAllFeedback() {
    return await database.readAll(collections.FEEDBACK, [
      orderBy('createdAt', 'desc')
    ]);
  },

  // Get feedback by category
  async getFeedbackByCategory(category) {
    return await database.readAll(collections.FEEDBACK, [
      where('category', '==', category),
      orderBy('createdAt', 'desc')
    ]);
  },

  // Get feedback by rating
  async getFeedbackByRating(minRating) {
    return await database.readAll(collections.FEEDBACK, [
      where('rating', '>=', minRating),
      orderBy('createdAt', 'desc')
    ]);
  }
};

const createTimestampFromDateAndTime = (dateString, timeString) => {
  if (!dateString) {
    return Timestamp.fromDate(new Date());
  }

  const safeTime = timeString || '00:00';
  const [hours = 0, minutes = 0] = safeTime.split(':').map((value) => parseInt(value, 10) || 0);
  const date = new Date(`${dateString}T00:00:00`);
  date.setHours(hours, minutes, 0, 0);

  return Timestamp.fromDate(date);
};

const sanitizeArray = (value) => (Array.isArray(value) ? value.filter((item) => !!item) : []);

export const migraineLogService = {
  async logEntry(entryData) {
    const {
      userId = 'demo-user',
      date,
      time,
      severity,
      duration,
      location,
      triggers,
      symptoms,
      notes,
      medications,
      weather,
      sleep,
      stress,
      mood,
      activities,
      isPredicted,
      predictionAccuracy
    } = entryData;

    const migraineDocument = {
      userId,
      date: createTimestampFromDateAndTime(date, time),
      severity: typeof severity === 'number' ? severity : parseInt(severity, 10) || 0,
      location: sanitizeArray(location),
      triggers: sanitizeArray(triggers),
      symptoms: sanitizeArray(symptoms)
    };

    if (duration) {
      migraineDocument.duration = duration;
    }

    if (notes) {
      migraineDocument.notes = notes;
    }

    if (medications) {
      migraineDocument.medications = sanitizeArray(medications);
    }

    if (weather) {
      migraineDocument.weather = weather;
    }

    if (sleep) {
      migraineDocument.sleep = sleep;
    }

    if (typeof stress === 'number') {
      migraineDocument.stress = stress;
    }

    if (typeof mood === 'number') {
      migraineDocument.mood = mood;
    }

    if (activities) {
      migraineDocument.activities = sanitizeArray(activities);
    }

    if (typeof isPredicted === 'boolean') {
      migraineDocument.isPredicted = isPredicted;
    }

    if (typeof predictionAccuracy === 'number') {
      migraineDocument.predictionAccuracy = predictionAccuracy;
    }

    return await database.create(collections.MIGRAINE_LOGS, migraineDocument);
  },

  async getRecentLogs(userId, limitCount = 20) {
    if (!userId) {
      throw new Error('User ID is required to fetch migraine logs');
    }

    return await database.readAll(collections.MIGRAINE_LOGS, [
      where('userId', '==', userId),
      orderBy('date', 'desc'),
      limit(limitCount)
    ]);
  }
};
